/*
 * Farida Gupta Blogs listing (theme/sections/fg-blog-tabs.liquid).
 *
 * Extends theme.js's <carousel-navigation> to feed a <progress-bar> from the tab bar's own
 * scrollLeft / scrollable-width ratio, the same pattern fg-autoscroll-gallery.js uses for its
 * carousel - <carousel-navigation> has no progress-bar support of its own since it's normally
 * short enough not to need one.
 */
import { CarouselNavigation } from 'theme';

class FgBlogTabsNavigation extends CarouselNavigation {
  #progressBar;
  #scrollFrame;

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('progress-bar')) {
      this.#progressBar = document.getElementById(this.getAttribute('progress-bar'));
      this.addEventListener('scroll', () => this.#queueProgressUpdate());
      this.#updateProgress();
    }
  }

  onNavigationChange(newIndex) {
    super.onNavigationChange(newIndex);
    this.#resetPageParam();
  }

  // Each tab paginates independently, but Shopify's `page` URL param is a single global value.
  // Clicking "Load more" on a real blog tab writes it into the address bar (fg-load-more.js), while
  // the "All Blogs" tab's "Load more" never touches the URL at all (fg-reveal-more.js). Left alone,
  // switching tabs leaves that `page` value stuck from whichever tab was open before - stale and
  // misleading regardless of which tab it belonged to. So every tab switch clears it, the same way
  // landing on a fresh listing page would.
  #resetPageParam() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('page')) return;

    url.searchParams.delete('page');
    history.replaceState({}, '', url.toString());
  }

  #queueProgressUpdate() {
    if (this.#scrollFrame) {
      return;
    }
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      this.#updateProgress();
    });
  }

  #updateProgress() {
    if (!this.#progressBar) {
      return;
    }
    const maxScroll = Math.max(1, Math.round(this.scrollWidth - this.clientWidth));
    this.#progressBar.setAttribute('aria-valuemax', maxScroll);
    this.#progressBar.setAttribute('aria-valuenow', Math.round(this.scrollLeft));
  }
}

if (!window.customElements.get('fg-blog-tabs-navigation')) {
  window.customElements.define('fg-blog-tabs-navigation', FgBlogTabsNavigation);
}
